import { useQuery } from "@tanstack/react-query";
import { apiService } from "../../services/apiService";
import { fileQueryKeys } from "../../services/query/fileQueryKeys";
import { QUERY_GC_TIME_MS } from "../../utils/memoryConfig";
import { billedVseStorageBytes } from "../../constants/uploadCapacity";
import { isPlaceholderProjectThumbnail } from "../../utils/filePathUtils";
import { canonicalViewrGcsPathForThumbnailCache } from "../../services/query/fileCacheSync";
import {
  classifyThumbnailUrl,
  traceProjectThumbnail,
} from "../../utils/thumbnailTrace";
import {
  getFinalFileThumbnailUrl,
  normalizeFinalFiles,
  preloadInitialFinalFileThumbnails,
} from "../../utils/finalFileThumbnails";
import type {
  FileData,
  SortOption,
  User,
} from "../../types";
import type {
  LibraryItemDetailResponse,
  LibraryItemResponse,
  SyncLibraryFromGcsResponse,
} from "../../services/api/library";
import type { StorageUsage } from "../../types/library.d";

const EMPTY_FILES: FileData[] = [];
const EMPTY_LIBRARY_ITEMS: LibraryItemResponse[] = [];
const BYTES_PER_GB = 1024 * 1024 * 1024;

const isGuestUser = (user: User | null): boolean =>
  Boolean(
    user?.isGuest ||
      user?.provider === "guest" ||
      user?.login_method === "guest" ||
      user?.uid?.startsWith("guest-"),
  );

const sortOptionToFinalListApi = (
  sort: SortOption,
): "last_viewed" | "date_created" =>
  sort === "dateCreated" ? "date_created" : "last_viewed";

export { normalizeFinalFile } from "../../utils/finalFileThumbnails";

const normalizeFinalFilesResponse = (files?: FileData[]): FileData[] =>
  normalizeFinalFiles(files);

const getFinalFileCanonicalPath = (file: FileData): string => {
  const path = file.gcs_path || file.full_path || file.path || "";
  return canonicalViewrGcsPathForThumbnailCache(path);
};

const findPreviousFinalFile = (
  previousFiles: FileData[],
  nextFile: FileData,
): FileData | undefined => {
  if (nextFile.id) {
    const idMatch = previousFiles.find((file) => file.id === nextFile.id);
    if (idMatch) return idMatch;
  }

  const nextPath = getFinalFileCanonicalPath(nextFile);
  if (!nextPath) return undefined;
  return previousFiles.find(
    (file) => getFinalFileCanonicalPath(file) === nextPath,
  );
};

/**
 * Home refetch가 늦게 도착한 placeholder로 Save 직후 프로젝트 썸네일을 되돌리지 않게 한다.
 * 실제 프로젝트 썸네일이 서버에서 오면 새 URL을 사용하고, placeholder/null일 때만 이전 정상 URL을 유지한다.
 */
export const mergeFinalFilesPreservingProjectThumbnails = (
  previousFiles: FileData[] | undefined,
  nextFiles: FileData[],
): FileData[] => {
  if (!previousFiles?.length) return nextFiles;

  return nextFiles.map((nextFile) => {
    const nextThumbnail = getFinalFileThumbnailUrl(nextFile);
    if (
      nextThumbnail &&
      !isPlaceholderProjectThumbnail(nextThumbnail, null)
    ) {
      return nextFile;
    }

    const previousFile = findPreviousFinalFile(previousFiles, nextFile);
    if (!previousFile) return nextFile;
    const previousThumbnail = getFinalFileThumbnailUrl(previousFile);
    if (
      !previousThumbnail ||
      isPlaceholderProjectThumbnail(previousThumbnail, null)
    ) {
      return nextFile;
    }

    traceProjectThumbnail("refetch_placeholder_rejected", {
      fileId: nextFile.id,
      gcsPath: getFinalFileCanonicalPath(nextFile),
      previousThumbnail,
      previousKind: classifyThumbnailUrl(previousThumbnail),
      incomingThumbnail: nextThumbnail,
      incomingKind: classifyThumbnailUrl(nextThumbnail),
    });

    return {
      ...nextFile,
      thumbnailUrl: previousThumbnail,
      thumbnail_url: previousThumbnail,
      thumbnail: previousThumbnail,
    };
  });
};

export const useFinalFilesQuery = ({
  currentUser,
  finalFilesSort = "lastViewed",
}: {
  currentUser: User | null;
  finalFilesSort?: SortOption;
}) =>
  useQuery<FileData[]>({
    queryKey: fileQueryKeys.finalFiles(currentUser?.uid, finalFilesSort),
    queryFn: async () => {
      if (!currentUser?.token) throw new Error("User not authenticated");
      const response = (await apiService.listFinalFiles(
        currentUser.token,
        sortOptionToFinalListApi(finalFilesSort),
      )) as { files?: FileData[] };
      const files = normalizeFinalFilesResponse(response.files);
      preloadInitialFinalFileThumbnails(files, undefined, currentUser.uid);
      return files;
    },
    enabled: !!currentUser?.token && !isGuestUser(currentUser),
    staleTime: 5 * 60 * 1000,
    gcTime: QUERY_GC_TIME_MS,
    structuralSharing: (previousFiles: unknown, nextFiles: FileData[]) =>
      mergeFinalFilesPreservingProjectThumbnails(
        Array.isArray(previousFiles) ? (previousFiles as FileData[]) : undefined,
        nextFiles,
      ),
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

export const useSampleFinalFilesQuery = ({
  currentUser,
}: {
  currentUser: User | null;
}) =>
  useQuery({
    queryKey: fileQueryKeys.sampleFinalFiles(currentUser?.uid),
    queryFn: async ({ signal }) => {
      if (!currentUser?.token) throw new Error("User not authenticated");
      const response = (await apiService.listSampleFinalFiles(
        currentUser.token,
        signal,
      )) as { files?: FileData[] };
      return normalizeFinalFilesResponse(response.files);
    },
    enabled: !!currentUser?.token,
    staleTime: 10 * 60 * 1000,
    gcTime: QUERY_GC_TIME_MS,
    retry: 2,
  });

export const useRawFilesQuery = ({ currentUser }: { currentUser: User | null }) =>
  useQuery({
    queryKey: fileQueryKeys.rawFiles(currentUser?.uid),
    queryFn: async (): Promise<FileData[]> => {
      if (!currentUser?.token) throw new Error("User not authenticated");
      return [];
    },
    enabled: !!currentUser?.token && !isGuestUser(currentUser),
    staleTime: 10 * 60 * 1000,
    gcTime: QUERY_GC_TIME_MS,
    retry: 0,
  });

export const useLibraryItemsQuery = ({
  currentUser,
  staleTime = 3 * 60 * 1000,
  refetchOnMount = false,
}: {
  currentUser: User | null;
  staleTime?: number;
  refetchOnMount?: boolean;
}) =>
  useQuery({
    queryKey: fileQueryKeys.libraryItems(currentUser?.uid),
    queryFn: async () => {
      if (!currentUser?.token) throw new Error("User not authenticated");
      return apiService.getLibraryItems(currentUser.token);
    },
    enabled: !!currentUser?.token && !isGuestUser(currentUser),
    staleTime,
    refetchOnMount,
    gcTime: QUERY_GC_TIME_MS,
    retry: 3,
  });

export const useConvertedFilesQuery = ({
  currentUser,
}: {
  currentUser: User | null;
}) =>
  useQuery({
    queryKey: fileQueryKeys.convertedFiles(currentUser?.uid),
    queryFn: async () => {
      if (!currentUser?.token) throw new Error("User not authenticated");
      const items = await apiService.getLibraryItems(currentUser.token);
      return items.map(
        (item): FileData => ({
          id: item.id,
          name: item.display_name,
          filename: item.display_name,
          gcs_path: item.gcs_path,
          full_path: item.gcs_path,
          size: item.size_bytes ?? undefined,
          created_at: item.created_at ?? undefined,
          updated_at: item.updated_at ?? undefined,
        }),
      );
    },
    enabled: !!currentUser?.token && !isGuestUser(currentUser),
    staleTime: 3 * 60 * 1000,
    gcTime: QUERY_GC_TIME_MS,
    retry: 3,
  });

export const useTrashFilesQuery = ({
  currentUser,
}: {
  currentUser: User | null;
}) =>
  useQuery({
    queryKey: fileQueryKeys.trashFiles(currentUser?.uid),
    queryFn: async () => {
      if (!currentUser?.token) throw new Error("User not authenticated");
      const response = (await apiService.listTrashFiles(currentUser.token)) as {
        files?: FileData[];
      };
      return normalizeFinalFilesResponse(response.files);
    },
    enabled: !!currentUser?.token && !isGuestUser(currentUser),
    staleTime: 60 * 1000,
    gcTime: QUERY_GC_TIME_MS,
    retry: 2,
  });

export const useStorageUsageQuery = ({
  currentUser,
}: {
  currentUser: User | null;
}) =>
  useQuery<StorageUsage>({
    queryKey: fileQueryKeys.storageUsage(currentUser?.uid),
    queryFn: async () => {
      if (!currentUser?.token) throw new Error("User not authenticated");
      const response = await apiService.getStorageUsage(currentUser.token);
      const usedGB =
        billedVseStorageBytes(response.used_bytes ?? 0) / BYTES_PER_GB;
      const totalGB = (response.quota_bytes ?? 100 * BYTES_PER_GB) / BYTES_PER_GB;
      const percentage = totalGB > 0 ? (usedGB / totalGB) * 100 : 0;

      return {
        used: usedGB,
        total: totalGB,
        percentage,
      };
    },
    enabled: !!currentUser?.token && !isGuestUser(currentUser),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });

export const useLibraryDetailQuery = ({
  token,
  uid,
  itemId,
  enabled = true,
}: {
  token: string | null;
  uid?: string | null;
  itemId: string | null | undefined;
  enabled?: boolean;
}) =>
  useQuery<LibraryItemDetailResponse>({
    queryKey: fileQueryKeys.libraryDetail(uid, itemId),
    queryFn: async () => {
      if (!token || !itemId) throw new Error("Library detail unavailable");
      return apiService.getLibraryItemDetail(token, itemId);
    },
    enabled: enabled && !!token && !!itemId,
    staleTime: 2 * 60 * 1000,
    gcTime: QUERY_GC_TIME_MS,
    retry: 2,
  });

export interface ProjectMetadata {
  metadata: unknown;
}

export const useProjectMetadataQuery = ({
  token,
  uid,
  modelName,
  enabled = true,
}: {
  token: string | null;
  uid: string | null | undefined;
  modelName: string | null | undefined;
  enabled?: boolean;
}) =>
  useQuery<ProjectMetadata>({
    queryKey: fileQueryKeys.projectMetadata(uid, modelName),
    queryFn: async () => {
      if (!token || !uid || !modelName) {
        throw new Error("Project metadata unavailable");
      }
      const metadata = await apiService.getMetadataFile(token, uid, modelName);
      return { metadata };
    },
    enabled: enabled && !!token && !!uid && !!modelName,
    staleTime: 5 * 60 * 1000,
    gcTime: QUERY_GC_TIME_MS,
    retry: 1,
  });

export const emptyFileQueryData = {
  files: EMPTY_FILES,
  libraryItems: EMPTY_LIBRARY_ITEMS,
};

export type SyncLibraryResult = SyncLibraryFromGcsResponse;
